import type { ReactNode } from 'react';
import { FOCUS_RING, TONE, type Tone } from './tone';
import { headerCheck, selectAllLabel, shownLabel, sortArrow, type SortDirection } from './tableRules';
import { Checkbox } from './Chips';

// Phase 36 §21b — table anatomy.
//
// Rows carry state on the LEFT EDGE, inside the card's rounded clip. Selected
// is fill + rule + weight together, never one of the three alone: a tint on its
// own disappears in greyscale and under the two most common colour-vision
// deficiencies, and a row you cannot tell is selected is a bulk action you
// cannot predict.

const MONO = 'co-mono';

export const CARD: React.CSSProperties = {
  // Both are tokens: the corner and the lift are a theme's two loudest
  // signals after colour, and a literal here would survive a theme swap.
  borderRadius: 'var(--co-radius-card)',
  boxShadow: 'var(--co-shadow-card)',
};

export const CARD_CLASS = 'border border-[var(--co-line)] bg-[var(--co-raised)]';

export type RowState = 'rest' | 'selected' | 'breach' | 'inactive';

export function TableRow({
  state = 'rest',
  onClick,
  children,
}: {
  state?: RowState;
  onClick?: () => void;
  children: ReactNode;
}) {
  const selected = state === 'selected';
  const style: React.CSSProperties = {
    background: selected
      ? 'var(--co-selected)'
      : state === 'inactive'
        ? 'var(--co-sunken)'
        : undefined,
    borderLeftColor: selected
      ? 'var(--co-brass)'
      : state === 'breach'
        ? 'var(--co-burgundy)'
        : 'transparent',
    fontWeight: selected ? 600 : undefined,
    opacity: state === 'inactive' ? 0.75 : undefined,
  };
  const className =
    'flex w-full items-center gap-3 border-b border-l-2 border-[var(--co-line-subtle)] ' +
    'px-[14px] py-[11px] text-left last:border-b-0 ' +
    (onClick ? 'hover:bg-[var(--co-selected)] ' : '') +
    FOCUS_RING;

  if (!onClick) {
    return (
      <div role="row" className={className} style={style}>
        {children}
      </div>
    );
  }
  return (
    <button type="button" role="row" onClick={onClick} className={className} style={style}>
      {children}
    </button>
  );
}

/**
 * The bulk bar. `Select all n` counts the SERVER total, not the loaded page.
 *
 * Offering "select all 25" on the first page of 340 and then acting on 340 —
 * or on 25 — is the same bug wearing two labels.
 */
export function BulkBar({
  selected,
  onPage,
  serverTotal,
  onSelectAll,
  children,
}: {
  selected: number;
  onPage: number;
  serverTotal: number;
  onSelectAll?: () => void;
  /** The actions. Destructive ones last. */
  children?: ReactNode;
}) {
  if (selected <= 0) return null;
  const all = selectAllLabel(onPage, serverTotal);
  return (
    <div
      className="flex flex-wrap items-center gap-[11px] rounded-lg border px-[14px] py-[10px]"
      style={{ borderColor: 'var(--co-brass-line)', background: 'var(--co-brass-wash)' }}
    >
      <span
        className={`${MONO} text-[9.5px] font-semibold uppercase tracking-[.16em] text-[var(--co-brass-ink)]`}
      >
        {`${selected.toLocaleString()} selected`}
      </span>
      {all && onSelectAll && (
        <button
          type="button"
          onClick={onSelectAll}
          className={`${MONO} text-[9.5px] uppercase tracking-[.1em] text-[var(--co-ink-3)] hover:text-[var(--co-ink)] ${FOCUS_RING}`}
        >
          {all}
        </button>
      )}
      <span className="h-4 w-px bg-[var(--co-brass-line)]" aria-hidden="true" />
      {children}
    </div>
  );
}

export function ColumnHeader({
  columns,
  sortBy,
  direction = 'desc',
  onSort,
  check,
}: {
  columns: readonly { id: string; label: string; width?: number; align?: 'left' | 'right' }[];
  sortBy?: string | null;
  direction?: SortDirection;
  onSort?: (id: string) => void;
  /** The header checkbox, when the table supports selection. */
  check?: { selected: number; onPage: number; onToggle: () => void };
}) {
  return (
    <div
      role="row"
      className={`${MONO} flex items-center gap-3 border-b border-[var(--co-line)] px-[14px] py-[11px] text-[9.5px] font-semibold uppercase tracking-[.2em] text-[var(--co-ink-3)]`}
    >
      {check && (
        <Checkbox
          label="Select all rows on this page"
          checked={headerCheck(check.selected, check.onPage) === 'all'}
          indeterminate={headerCheck(check.selected, check.onPage) === 'some'}
          onChange={check.onToggle}
        />
      )}
      {columns.map((column) => {
        const arrow = sortArrow(column.id, sortBy, direction);
        const inner = (
          <>
            {column.label}
            {/* Only on the column the rows are actually ordered by. A grey
                arrow on every heading says "sortable" and says nothing about
                the order the reader is looking at. */}
            {arrow && <span className="ml-1 text-[var(--co-ink)]">{arrow}</span>}
          </>
        );
        const style = column.width
          ? { width: column.width, flex: 'none' as const }
          : { flex: 1, minWidth: 0 };
        const align = column.align === 'right' ? 'text-right' : '';
        return onSort ? (
          <button
            key={column.id}
            type="button"
            role="columnheader"
            aria-sort={
              arrow ? (direction === 'asc' ? 'ascending' : 'descending') : 'none'
            }
            onClick={() => onSort(column.id)}
            className={`${align} ${FOCUS_RING} hover:text-[var(--co-ink)]`}
            style={style}
          >
            {inner}
          </button>
        ) : (
          <div key={column.id} role="columnheader" className={align} style={style}>
            {inner}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Says what the emptiness MEANS.
 *
 * "No data" is true of every empty table and useful on none of them. "Nothing
 * unassigned — every conversation has an owner" tells the reader whether they
 * are looking at good news or a broken filter.
 */
export function EmptyState({ title, body, action }: { title: string; body: string; action?: ReactNode }) {
  return (
    <div className="px-5 py-8 text-center">
      <p className="text-[13.5px] font-semibold text-[var(--co-ink)]">{title}</p>
      <p className="mx-auto mt-[6px] max-w-[42ch] text-[12.5px] leading-[1.55] text-[var(--co-ink-3)]">
        {body}
      </p>
      {action && <div className="mt-5 flex justify-center">{action}</div>}
    </div>
  );
}

/**
 * Stale rows at 45%, not a spinner.
 *
 * Replacing content with a spinner throws away the numbers the reader was
 * already reading and teaches them to look away while it loads. Dimming keeps
 * the previous answer legible and marks it as not-yet-current.
 */
export function LoadingRows({ rows = 3 }: { rows?: number }) {
  return (
    <div aria-busy="true" style={{ opacity: 0.45 }}>
      {Array.from({ length: rows }, (_, i) => (
        <div
          key={i}
          className="flex items-center gap-3 border-b border-[var(--co-line-subtle)] px-[14px] py-[13px] last:border-b-0"
        >
          <div className="h-[11px] w-[44%] rounded-[5px] bg-[var(--co-line-subtle)]" />
          <div className="flex-1" />
          <div className="h-[11px] w-[54px] rounded-[5px] bg-[var(--co-line-subtle)]" />
        </div>
      ))}
    </div>
  );
}

/**
 * The foot: what is shown, and the caveat.
 *
 * It states the ordering and what is excluded, because a truncated list that
 * does not say so is a list the reader will take for the whole set.
 */
export function CardFoot({
  left,
  note,
  counts,
}: {
  left?: string;
  note?: ReactNode;
  /** Pass instead of `left` to have the count phrased for you. */
  counts?: { onPage: number; serverTotal: number };
}) {
  return (
    <div className="flex flex-none flex-wrap items-center gap-x-4 gap-y-1 border-t border-[var(--co-line)] bg-[var(--co-selected)] px-5 py-3">
      <span
        className={`${MONO} whitespace-nowrap text-[10.5px] uppercase tracking-[.1em] text-[var(--co-ink-3)]`}
      >
        {counts ? shownLabel(counts.onPage, counts.serverTotal) : left}
      </span>
      <div className="flex-1" />
      {note && <span className="text-[12px] leading-[1.5] text-[var(--co-ink-3)]">{note}</span>}
    </div>
  );
}

/** A row-edge rule in a tone, for tables that mark rows without a chip. */
export function edgeColor(tone: Tone | null | undefined): string {
  return tone ? TONE[tone].ink : 'transparent';
}
